import {useState, type FormEvent} from 'react'
import {Link} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {FieldError} from '@/components/field-error'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {Label} from '@/components/ui/label'
import {PorQue} from '@/components/ui/por-que'
import {HelpIcon} from '@/components/help-icon'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {TrazabilidadSwitch, useTrazabilidad} from '@/components/perfil/provenance-note'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {Textarea} from '@/components/ui/textarea'
import {useCapabilities} from '@/hooks/empresa/organizacion/use-businesses'
import {useGodModeReadOnly} from '@/hooks/platform/use-god-mode'
import {
  SUGERENCIAS_PAGE_SIZE,
  useDecidirSugerencia,
  useSugerenciasLista,
  useSugerenciasPerfil,
  type PerfilSugerencia,
} from '@/hooks/empresa/perfil_cargo/use-perfiles-cargo'
import {translateApiError} from '@/lib/errors'
import {formatInstantDate} from '@/lib/risk'
import {validateRequired} from '@/lib/validation'
import {tope} from '@/lib/campos-texto'

function personasDe(sugerencia: PerfilSugerencia): string {
  const personas = (sugerencia.origen_json.personas as string[] | undefined) ?? []
  const entrevistas = (sugerencia.origen_json.entrevistas as string[] | undefined) ?? []
  if (personas.length === 0) return ''
  return ` (${personas.join(', ')}${entrevistas.length > 0 ? ` · ${entrevistas.join(', ')}` : ''})`
}

function Decidida({sugerencia, desplegado}: {sugerencia: PerfilSugerencia; desplegado: boolean}) {
  const {t} = useTranslation()
  if (!desplegado) return null
  return (
    <PorQue
      label={t('common.deDondeSalio')}
      titulo={t('common.deDondeSalio')}
      ariaLabel={`${t('common.deDondeSalio')} — ${sugerencia.texto}`}
      contenidoTestId={`sugerencia-decidida-contenido-${sugerencia.id}`}
      siempreVisible>
      <p>
        {t(`perfilAprende.decidida.${sugerencia.estado}`, {
          nombre: sugerencia.decidido_por_nombre ?? t('perfilAprende.operadorPlataforma'),
          fecha: sugerencia.decidido_en ? formatInstantDate(sugerencia.decidido_en) : '',
        })}
        {sugerencia.no_aplica_motivo && ` — ${sugerencia.no_aplica_motivo}`}
      </p>
    </PorQue>
  )
}

type Accion = 'agregar' | 'no_aplica'

/** «No aplica» no toca el perfil; «Agregar/Quitar» lo deja en borrador hasta que alguien lo firme. */
function claveDelDialogo(accion: Accion, signo: PerfilSugerencia['signo']): string {
  if (accion === 'no_aplica') return 'descarte'
  return signo === 'mas' ? 'cambioAgregar' : 'cambioQuitar'
}

export function PerfilAprendeSection({businessId, positionId}: {businessId: string; positionId: string}) {
  const {t} = useTranslation()
  const godReadOnly = useGodModeReadOnly()
  // Decidir es acto de firmante: la capacidad la computa el backend y la UI no la re-deriva.
  const capabilities = useCapabilities(businessId)
  const sugerencias = useSugerenciasPerfil(businessId, positionId)
  const decidir = useDecidirSugerencia(businessId, positionId)
  // Las listas esperan a la cabecera: ese GET es el que aprende y sincroniza las filas que leen.
  const [paginaPendientes, setPaginaPendientes] = useState(1)
  const [paginaDecididas, setPaginaDecididas] = useState(1)
  const hayCabecera = sugerencias.data?.disponible === true
  const pendientes = useSugerenciasLista(businessId, positionId, false, paginaPendientes, hayCabecera)
  const decididas = useSugerenciasLista(businessId, positionId, true, paginaDecididas, hayCabecera)
  const [aDecidir, setADecidir] = useState<{sugerencia: PerfilSugerencia; accion: Accion} | null>(null)
  const [motivo, setMotivo] = useState('')
  const [errorMotivo, setErrorMotivo] = useState<string | undefined>()
  const [error, setError] = useState<string | null>(null)
  const [trazabilidad, setTrazabilidad] = useTrazabilidad()

  if (sugerencias.isPending) return <Skeleton className="h-32 w-full" data-testid="perfil-aprende-cargando" />
  if (sugerencias.isError) {
    return (
      <p className="notice-amber rounded-lg px-3 py-2 text-sm" data-testid="perfil-aprende-error">
        {t('perfilAprende.errors.cargaFallida')}
      </p>
    )
  }
  const datos = sugerencias.data
  // Sin perfil firmado no hay nada que aprender; un cargo que ya tuvo versión firmada conserva la
  // sección aunque tenga un borrador encima.
  if (!datos.disponible) return null
  const puedeDecidir = capabilities.sgsst_firmar && !godReadOnly
  const clave = aDecidir ? claveDelDialogo(aDecidir.accion, aDecidir.sugerencia.signo) : 'descarte'

  function enviar(evento: FormEvent) {
    evento.preventDefault()
    const fallo = validateRequired(motivo)
    setErrorMotivo(fallo ? t(fallo) : undefined)
    if (fallo || !aDecidir) return
    setError(null)
    decidir.mutate(
      {sugerenciaId: aDecidir.sugerencia.id, accion: aDecidir.accion, motivo: motivo.trim()},
      {
        onSuccess: () => {
          setADecidir(null)
          setMotivo('')
        },
        onError: (fallo) => setError(translateApiError(fallo)),
      }
    )
  }

  function abrir(sugerencia: PerfilSugerencia, accion: Accion) {
    setADecidir({sugerencia, accion})
    setMotivo('')
    setErrorMotivo(undefined)
    setError(null)
  }

  return (
    <section className="surface space-y-4 rounded-lg p-4" data-testid="perfil-aprende">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <h3 className="text-sm font-semibold">{t('perfilAprende.titulo')}</h3>
          <HelpIcon
            label={t('common.help.iconLabelFor', {campo: t('perfilAprende.titulo')})}
            testId="perfil-aprende-encabezado">
            <p>{t('perfilAprende.encabezado')}</p>
            <p className="mt-1.5" data-testid="perfil-aprende-sin-inspecciones">
              {t('perfilAprende.sinInspecciones')}
            </p>
            {!datos.lector_modelo && (
              <p className="mt-1.5" data-testid="perfil-aprende-sin-lectura">
                {t('perfilAprende.sinLecturaDeTexto')}
              </p>
            )}
          </HelpIcon>
        </div>
        <TrazabilidadSwitch desplegado={trazabilidad} onChange={setTrazabilidad} testId="perfil-aprende-trazabilidad" />
      </div>
      {error && !aDecidir && (
        <p className="notice-amber rounded-lg px-3 py-2 text-sm" data-testid="perfil-aprende-error-accion">
          {error}
        </p>
      )}
      {datos.borrador_version !== null && (
        <div className="flex flex-wrap items-center gap-2" data-testid="perfil-aprende-borrador">
          <Tag tone="amber">{t('perfilAprende.borrador', {version: datos.borrador_version})}</Tag>
          <Link
            className="text-xs font-medium text-primary hover:underline"
            to={`/businesses/${businessId}/sgsst/perfiles-cargo/${positionId}`}>
            {t('perfilAprende.borradorEnlace')}
          </Link>
          <PorQue
            label={t('perfilAprende.borradorPorqueLabel')}
            testId="perfil-aprende-borrador-porque"
            contenidoTestId="perfil-aprende-borrador-porque-contenido">
            {t('perfilAprende.borradorPorque')}
          </PorQue>
        </div>
      )}
      {datos.total_personas === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="perfil-aprende-sin-titulares">
          {t('perfilAprende.sinTitulares')}
        </p>
      ) : (
        datos.con_entrevista === 0 && (
          <p className="text-sm text-muted-foreground" data-testid="perfil-aprende-sin-entrevistas">
            {t('perfilAprende.nadieContesto')}
          </p>
        )
      )}
      {!datos.propone_menos && (
        <PorQue
          label={t('common.porQue')}
          ariaLabel={`${t('common.porQue')} — ${t('perfilAprende.signo.menos')}`}
          testId="perfil-aprende-sin-menos"
          contenidoTestId="perfil-aprende-sin-menos-contenido">
          <p>
            {t('perfilAprende.sinMenos', {
              minimo: datos.minimo_entrevistas,
              meses: datos.meses_sin_declarar,
              entrevistas: datos.entrevistas_en_periodo,
            })}
          </p>
          {datos.fuera_del_menos > 0 && (
            <p data-testid="perfil-aprende-fuera-del-menos">
              {t('perfilAprende.fueraDelMenos', {count: datos.fuera_del_menos})}
            </p>
          )}
        </PorQue>
      )}
      {datos.pendientes_total === 0 && (
        <p className="text-sm text-muted-foreground" data-testid="perfil-aprende-vacia">
          {t('perfilAprende.vacia')}
        </p>
      )}
      <ul className="space-y-3">
        {(pendientes.data?.items ?? []).map((sugerencia) => (
          <li key={sugerencia.id} className="space-y-1 border-t pt-3" data-testid={`sugerencia-${sugerencia.id}`}>
            <p className="text-sm">
              <Tag tone="neutral" className="mr-2">
                {t(`perfilAprende.signo.${sugerencia.signo}`)}
              </Tag>
              {sugerencia.texto}
            </p>
            {trazabilidad && (
              <PorQue
                label={t('common.deDondeSalio')}
                titulo={t('common.deDondeSalio')}
                ariaLabel={`${t('common.deDondeSalio')} — ${sugerencia.texto}`}
                contenidoTestId={`sugerencia-origen-contenido-${sugerencia.id}`}
                siempreVisible>
                <p data-testid={`sugerencia-bloque-${sugerencia.id}`}>
                  {t('perfilAprende.entraA', {bloque: t(`perfilAprende.bloque.${sugerencia.bloque}`)})}
                  {sugerencia.es_ia
                    ? ` · ${t('perfilAprende.leidaDelTexto')}`
                    : ` · ${t('perfilAprende.reproducible')}`}
                </p>
                <p data-testid={`sugerencia-conteo-${sugerencia.id}`}>
                  {/* Plural por i18next: «1 de 1 personas» leería la señal débil como un descuido. */}
                  {t('perfilAprende.conteo', {
                    count: sugerencia.total_personas,
                    declarantes: sugerencia.conteo_declarantes,
                    total: sugerencia.total_personas,
                  })}
                  {sugerencia.sin_entrevista > 0 &&
                    `, ${t('perfilAprende.sinEntrevista', {
                      count: sugerencia.sin_entrevista,
                      sin: sugerencia.sin_entrevista,
                      total: sugerencia.total_personas,
                    })}`}
                  {personasDe(sugerencia)}
                </p>
                {!!sugerencia.origen_json.reaparicion && (
                  <p data-testid={`sugerencia-reaparicion-${sugerencia.id}`}>{t('perfilAprende.reaparicion')}</p>
                )}
              </PorQue>
            )}
            {puedeDecidir && (
              <div className="flex flex-wrap gap-2 pt-1">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={decidir.isPending}
                  data-testid={`sugerencia-agregar-${sugerencia.id}`}
                  onClick={() => abrir(sugerencia, 'agregar')}>
                  {t(`perfilAprende.acciones.${sugerencia.signo === 'mas' ? 'agregar' : 'quitar'}`)}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={decidir.isPending}
                  data-testid={`sugerencia-no-aplica-${sugerencia.id}`}
                  onClick={() => abrir(sugerencia, 'no_aplica')}>
                  {t('perfilAprende.acciones.noAplica')}
                </Button>
              </div>
            )}
          </li>
        ))}
      </ul>
      {datos.pendientes_total > 0 && (
        <PaginationBar
          page={paginaPendientes}
          pageSize={SUGERENCIAS_PAGE_SIZE}
          total={datos.pendientes_total}
          onPageChange={setPaginaPendientes}
        />
      )}
      {datos.decididas_total > 0 && (
        <>
          <ul className="space-y-2 border-t pt-3" data-testid="perfil-aprende-decididas">
            {(decididas.data?.items ?? []).map((sugerencia) => (
              <li key={sugerencia.id} className="space-y-0.5">
                <p className="text-sm text-muted-foreground">{sugerencia.texto}</p>
                <Decidida sugerencia={sugerencia} desplegado={trazabilidad} />
              </li>
            ))}
          </ul>
          <PaginationBar
            page={paginaDecididas}
            pageSize={SUGERENCIAS_PAGE_SIZE}
            total={datos.decididas_total}
            onPageChange={setPaginaDecididas}
          />
        </>
      )}
      <Dialog open={!!aDecidir} onOpenChange={(abierto) => !abierto && setADecidir(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t(`perfilAprende.${clave}.titulo`)}</DialogTitle>
            <DialogDescription>{t(`perfilAprende.${clave}.descripcion`)}</DialogDescription>
          </DialogHeader>
          <form className="space-y-3" noValidate onSubmit={enviar} data-testid="sugerencia-descarte-form">
            <div className="space-y-1">
              <Label htmlFor="sugerencia-motivo">{t(`perfilAprende.${clave}.motivo`)}</Label>
              <Textarea
                maxLength={tope('NOTA')}
                id="sugerencia-motivo"
                value={motivo}
                onBlur={() => {
                  const fallo = validateRequired(motivo)
                  setErrorMotivo(fallo ? t(fallo) : undefined)
                }}
                onChange={(evento) => setMotivo(evento.target.value)}
                data-testid="sugerencia-motivo"
              />
              <FieldError error={errorMotivo} />
            </div>
            <FormDialogFooter
              error={error}
              submitLabel={t(`perfilAprende.${clave}.guardar`)}
              pending={decidir.isPending}
              onCancel={() => setADecidir(null)}
            />
          </form>
        </DialogContent>
      </Dialog>
    </section>
  )
}
